import {
  IAutoExceptionTelemetry,
  IEventTelemetry,
  IExceptionTelemetry,
  IMetricTelemetry,
  IPageViewPerformanceTelemetry,
  IPageViewTelemetry,
  ITelemetryItem,
  ITraceTelemetry,
} from '@microsoft/applicationinsights-web';

import { getAppInsights } from './applicationInsights';

// Flag for development purposes, set to true for AppInsights written to console.
// In prod this boolean makes no differ
const writeAiToConsole = false;

const appInsights = getAppInsights();

export default class Logger {
  private readonly fileNamePrefix: string;

  constructor(fileName: string) {
    this.fileNamePrefix = fileName;
  }

  public log(message?: unknown, ...optionalParams: unknown[]) {
    console.log(this.fileNamePrefix, '-', message, optionalParams);
  }

  public error(message?: unknown, ...optionalParams: unknown[]) {
    console.error(this.fileNamePrefix, '-', message, optionalParams);
  }

  public warn(message?: unknown, ...optionalParams: unknown[]) {
    console.warn(this.fileNamePrefix, '-', message, optionalParams);
  }

  public info(message?: unknown, ...optionalParams: unknown[]) {
    console.info(this.fileNamePrefix, '-', message, optionalParams);
  }

  public trackTrace(trace: ITraceTelemetry, customProperties?: { [key: string]: unknown }) {
    appInsights?.trackTrace(trace, customProperties);

    if (writeAiToConsole) {
      this.log(trace.message);
    }
  }

  public trackException(exception: IExceptionTelemetry, customProperties?: { [key: string]: unknown }) {
    appInsights?.trackException(exception, customProperties);

    if (writeAiToConsole) {
      this.error(exception.exception?.message);
    }
  }

  public trackPageView(pageView: IPageViewTelemetry) {
    appInsights?.trackPageView(pageView);

    if (writeAiToConsole) {
      this.log(pageView.name);
    }
  }

  public trackMetric(metric: IMetricTelemetry, customProperties?: { [key: string]: unknown }) {
    appInsights?.trackMetric(metric, customProperties);

    if (writeAiToConsole) {
      this.log(metric.name);
    }
  }

  public trackEvent(event: IEventTelemetry, customProperties?: { [key: string]: unknown }) {
    appInsights?.trackEvent(event, customProperties);

    if (writeAiToConsole) {
      this.log(event.name);
    }
  }

  public trackPageViewPerformance(pageViewPerformance: IPageViewPerformanceTelemetry) {
    appInsights?.trackPageViewPerformance(pageViewPerformance);
  }

  public startTrackEvent(name: string) {
    appInsights?.startTrackEvent(name);
  }

  public stopTrackEvent(
    name: string,
    properties?: { [key: string]: string },
    measurements?: { [key: string]: number },
  ) {
    appInsights?.stopTrackEvent(name, properties, measurements);
  }

  public startTrackPage(name?: string) {
    appInsights?.startTrackPage(name);
  }

  public stopTrackPage(name?: string, url?: string, customProperties?: { [key: string]: unknown }) {
    appInsights?.stopTrackPage(name, url, customProperties);
  }

  public addTelemetryInitializer(telemetryInitializer: (item: ITelemetryItem) => boolean | void) {
    appInsights?.addTelemetryInitializer(telemetryInitializer);
  }

  public _onerror(exception: IAutoExceptionTelemetry) {
    appInsights?._onerror(exception);
  }

  /**
   * Log an error with type to AI. This is a simplified function to avoid using Telemetry properties when calling
   * @param message The message
   * @param type ErrorType
   * @param status statuscode if available
   */
  public logError(message: string, type: string, status?: number) {
    this.trackException({
      properties: {
        ErrorType: type,
        ErrorDetail: message,
        ErrorStatus: status,
      },
      exception: new Error(message),
    });
  }
}
